/**
 * A line's Dates card on the staff order page (v1 completion N4, mock-up state 7): one card per item line, under the
 * Order step's rows. A dated line shows the Speed it was ordered on and who it was timed with, a table of needed-by,
 * approve-by, place-by and the date the customer sees (with its state), and "What we told the customer" as a timeline.
 * A line that is "Check with our team" shows why instead, with the two commands that settle it.
 *
 * A from-stock line the warehouse holds only in part shows a Stock row: what is allocated and can go now and what is on
 * back order (P22). Its Arrives row is when all of it has arrived, or why there is no date.
 *
 * Every date and sentence is the backend's (Time's wording, orders' labels), the buttons' words included. The commands
 * are orders' (`confirm_order_line_date`, `repromise_order_line`) as the object contract presents them, enabled or with
 * why not; running one is the page's (`onRunCommand` opens the command shell).
 */
import type { Command } from "@shared-core/domain/object-ui/contract";
import type {
  LineDatesView,
  LinePromiseView,
  SupplierDateView,
} from "@shared-core/data/order-to-invoice-types";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Card, CardContent } from "../../primitives/card";
import { cn } from "../../primitives/utils";

/** The commands this card owns, in the order they are offered. */
export const LINE_DATE_COMMANDS = [
  "orders.confirm_order_line_date",
  "orders.repromise_order_line",
] as const;

type PromiseState = LinePromiseView["state"];

const STATE_VARIANT: Record<PromiseState, "secondary" | "success" | "warning"> =
  {
    projected: "secondary",
    firm: "success",
    revised: "warning",
    to_be_confirmed: "warning",
  };

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <tr className="border-t first:border-t-0">
      <th
        scope="row"
        className="py-2 pr-4 text-left align-top text-xs font-normal text-muted-foreground"
      >
        {label}
      </th>
      <td className="py-2 align-top">{children}</td>
    </tr>
  );
}

function ArrivalValue({ dates }: { dates: LineDatesView }) {
  if (!dates.arrival_label) {
    return <span>{dates.no_date_reason ?? "—"}</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2">
      <strong>{dates.arrival_label}</strong>
      {dates.arrival_state && dates.arrival_state_label && (
        <Badge variant={STATE_VARIANT[dates.arrival_state]}>
          {dates.arrival_state_label}
        </Badge>
      )}
      {dates.was_label && (
        <span className="text-xs text-muted-foreground">
          was {dates.was_label}
        </span>
      )}
    </span>
  );
}

/** What the supplier says about the line, how sure that is, and how much of the buffer it has spent (N5). */
function SupplierExpectsValue({ dates }: { dates: LineDatesView }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2">
      {dates.supplier_expects_label && (
        <span>{dates.supplier_expects_label}</span>
      )}
      {dates.supplier_expects_note && (
        <Badge variant={dates.is_expected_date_unknown ? "warning" : "outline"}>
          {dates.supplier_expects_note}
        </Badge>
      )}
      {dates.supplier_first_said_label && (
        <span className="text-xs text-muted-foreground">
          {dates.supplier_first_said_label}
        </span>
      )}
      {dates.buffer_label && (
        <span className="text-xs text-muted-foreground">
          buffer spent: {dates.buffer_label}
        </span>
      )}
    </span>
  );
}

type TimelineEntry =
  | { at: string; kind: "promise"; promise: LinePromiseView }
  | { at: string; kind: "supplier"; supplier: SupplierDateView };

/** The promises and what the supplier said, oldest first: the mock-up's one timeline. */
function timelineOf(dates: LineDatesView): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    ...dates.promises.map((promise): TimelineEntry => ({
      at: promise.at,
      kind: "promise",
      promise,
    })),
    ...dates.supplier_dates.map((supplier): TimelineEntry => ({
      at: supplier.at,
      kind: "supplier",
      supplier,
    })),
  ];
  return entries.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

function CommandButtons({
  dates,
  commands,
  lineLabel,
  busy,
  onRunCommand,
}: {
  dates: LineDatesView;
  commands: readonly Command[];
  lineLabel: string;
  busy: boolean;
  onRunCommand?: (command: Command, lineLabel: string) => void;
}) {
  const own = LINE_DATE_COMMANDS.map((name) =>
    commands.find((c) => c.name === name),
  ).filter((c): c is Command => c !== undefined);
  if (own.length === 0 || !onRunCommand) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {own.map((command) => {
        const isConfirm = command.name === "orders.confirm_order_line_date";
        const label = isConfirm
          ? (dates.confirm_label ?? command.label)
          : dates.repromise_label;
        return (
          <Button
            key={command.name}
            size="sm"
            variant={isConfirm ? "default" : "outline"}
            disabled={busy || !command.enabled}
            title={command.reason ?? undefined}
            aria-label={`${label} (${lineLabel})`}
            onClick={() => onRunCommand(command, lineLabel)}
          >
            {label}
          </Button>
        );
      })}
    </div>
  );
}

export function LineDatesCard({
  dates,
  commands,
  lineLabel,
  busy = false,
  onRunCommand,
}: {
  dates: LineDatesView;
  /** The line's commands (all of them; the card shows its own). */
  commands: readonly Command[];
  /** "ORD-K51302.02 · 250 roller banners": names the card and its buttons. */
  lineLabel: string;
  busy?: boolean;
  onRunCommand?: (command: Command, lineLabel: string) => void;
}) {
  return (
    <Card>
      <CardContent>
        <section aria-label={`Dates of ${lineLabel}`} className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <h3 className="text-base font-medium">{lineLabel}</h3>
            <div className="flex flex-wrap items-center gap-2">
              {dates.is_to_be_confirmed ? (
                <Badge variant="warning">
                  {dates.arrival_state_label ?? "Check with our team"}
                </Badge>
              ) : (
                dates.speed_sentence && (
                  <Badge variant="outline">Speed: {dates.speed_sentence}</Badge>
                )
              )}
              {dates.route_label && (
                <span className="font-mono text-xs text-muted-foreground">
                  {dates.route_label}
                </span>
              )}
            </div>
          </div>

          {dates.is_to_be_confirmed ? (
            <>
              <p className="text-sm">
                {dates.speed_sentence && <>Speed: {dates.speed_sentence}. </>}
                {dates.to_be_confirmed_note}
              </p>
              <CommandButtons
                dates={dates}
                commands={commands}
                lineLabel={lineLabel}
                busy={busy}
                onRunCommand={onRunCommand}
              />
            </>
          ) : (
            <div className="grid gap-x-8 gap-y-4 md:grid-cols-2">
              <table className="w-full text-sm">
                <tbody>
                  {dates.needed_by_label && (
                    <Row label="Needed by">{dates.needed_by_label}</Row>
                  )}
                  {dates.approve_by_label && (
                    <Row label="Approve by">
                      <span className="inline-flex flex-wrap items-center gap-x-2">
                        {dates.approve_by_label}
                        {dates.approved_label && (
                          <Badge variant="success">
                            {dates.approved_label}
                          </Badge>
                        )}
                      </span>
                    </Row>
                  )}
                  {dates.place_by_label && (
                    <Row label="Place by">
                      <span className="inline-flex flex-wrap items-center gap-x-2">
                        {dates.place_by_label}
                        {dates.placed_label && (
                          <Badge variant="success">{dates.placed_label}</Badge>
                        )}
                      </span>
                    </Row>
                  )}
                  {(dates.supplier_expects_label ||
                    dates.is_expected_date_unknown) && (
                    <Row label="Supplier expects">
                      <SupplierExpectsValue dates={dates} />
                    </Row>
                  )}
                  {dates.stock_parts_label && (
                    <Row label="Stock">{dates.stock_parts_label}</Row>
                  )}
                  <Row label="Arrives">
                    <ArrivalValue dates={dates} />
                  </Row>
                  {dates.projected_label && (
                    <Row label="Now projected">{dates.projected_label}</Row>
                  )}
                </tbody>
              </table>
              <div className="space-y-2">
                <h4 className="text-sm font-medium">
                  What we told the customer
                </h4>
                <ol className="space-y-1 border-l-2 pl-3 text-sm">
                  {timelineOf(dates).map((entry, i, all) =>
                    entry.kind === "promise" ? (
                      <li
                        key={`${entry.at}-${entry.promise.state}-${i}`}
                        className={cn(i === all.length - 1 && "font-medium")}
                      >
                        <span className="text-muted-foreground">
                          {entry.promise.at_label}
                        </span>{" "}
                        · {entry.promise.sentence}
                        {entry.promise.note && (
                          <span className="block text-xs font-normal text-muted-foreground">
                            {entry.promise.note}
                          </span>
                        )}
                        {!entry.promise.customer_told_at && (
                          <span className="block text-xs font-normal text-muted-foreground">
                            Not told to the customer
                          </span>
                        )}
                      </li>
                    ) : (
                      <li
                        key={`${entry.at}-supplier-${i}`}
                        className={cn(i === all.length - 1 && "font-medium")}
                      >
                        <span className="text-muted-foreground">
                          {entry.supplier.at_label}
                        </span>{" "}
                        · {entry.supplier.sentence}
                      </li>
                    ),
                  )}
                </ol>
                <CommandButtons
                  dates={dates}
                  commands={commands}
                  lineLabel={lineLabel}
                  busy={busy}
                  onRunCommand={onRunCommand}
                />
              </div>
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
